import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { environment } from 'src/environments/environment';
import {
  type FlavorColorToken,
  type FlavorColors,
  type FlavorConfig,
  type FlavorUiConfig,
  type IonicColorScale,
  LEGACY_IONIC_PALETTES,
  LEGACY_FLAVOR_UI,
  getFlavorConfig,
} from '../config/flavors';

@Injectable({ providedIn: 'root' })
export class FlavorService {
  private readonly document = inject(DOCUMENT);

  readonly config: FlavorConfig = getFlavorConfig(environment.flavor);
  readonly ui: FlavorUiConfig = LEGACY_FLAVOR_UI[this.config.id];

  initialize(): void {
    const root = this.document.documentElement;
    root.setAttribute('data-flavor', this.config.id);
    root.setAttribute('data-theme-id', this.config.defaultThemeId);

    this.applyPalette(this.config.colors);
    this.applyLegacyIonicPalette();
    this.applyLegacyUiVariables();
  }

  private applyPalette(colors: FlavorColors): void {
    const root = this.document.documentElement;

    root.style.setProperty('--brand-primary', colors.primary);
    root.style.setProperty('--brand-secondary', colors.secondary);
    root.style.setProperty('--brand-tertiary', colors.tertiary);
    root.style.setProperty('--brand-background', colors.background);
    root.style.setProperty('--brand-text', colors.text);
    root.style.setProperty('--brand-muted-text', colors.mutedText);
    root.style.setProperty('--brand-border', colors.border);
  }

  getThemeAssetPath(fileName: string): string {
    const logoPath = this.config.logoPath;
    const lastSlash = logoPath.lastIndexOf('/');
    const folderPath = lastSlash > 'assets'.length
      ? logoPath.slice(0, lastSlash)
      : 'assets';

    return `${folderPath}/${fileName}`;
  }

  private applyLegacyIonicPalette(): void {
    const palette = LEGACY_IONIC_PALETTES[this.config.id];
    this.applyIonicColor('primary', palette.primary);
    this.applyIonicColor('secondary', palette.secondary);
    this.applyIonicColor('tertiary', palette.tertiary);
  }

  private applyIonicColor(
    name: 'primary' | 'secondary' | 'tertiary',
    scale: IonicColorScale
  ): void {
    const root = this.document.documentElement;

    root.style.setProperty(`--ion-color-${name}`, scale.base);
    root.style.setProperty(`--ion-color-${name}-rgb`, scale.rgb);
    root.style.setProperty(`--ion-color-${name}-contrast`, scale.contrast);
    root.style.setProperty(`--ion-color-${name}-contrast-rgb`, scale.contrastRgb);
    root.style.setProperty(`--ion-color-${name}-shade`, scale.shade);
    root.style.setProperty(`--ion-color-${name}-tint`, scale.tint);
  }

  private applyLegacyUiVariables(): void {
    const root = this.document.documentElement;
    const virtualCard = this.ui.virtualCard;
    const cardBackgroundColor =
      this.resolveColorToken(this.ui.cardNumberBackgroundColor) ||
      this.config.colors.background;

    root.style.setProperty('--brand-toolbar-min-height', this.ui.toolbarMinHeight);
    root.style.setProperty('--brand-toolbar-image-size', this.ui.toolbarImageSize);
    root.style.setProperty('--brand-card-number-background', cardBackgroundColor);
    root.style.setProperty(
      '--brand-card-action-color',
      this.resolveColorToken(this.ui.cardActionColor) || this.config.colors.primary
    );

    const headerSeparatorColor = this.resolveColorToken(this.ui.headerSeparatorColor);
    root.style.setProperty(
      '--brand-header-separator-color',
      headerSeparatorColor || 'transparent'
    );

    root.style.setProperty(
      '--brand-dashboard-background',
      this.resolveColorToken(this.ui.dashboardBackgroundColor) || 'var(--ion-color-primary)'
    );
    root.style.setProperty(
      '--brand-dashboard-image',
      this.ui.dashboardBackgroundImage ? `url("${this.ui.dashboardBackgroundImage}")` : 'none'
    );
    root.style.setProperty(
      '--brand-dashboard-balance-color',
      this.resolveColorToken(this.ui.dashboardBalanceColor) || 'var(--ion-color-white)'
    );
    root.style.setProperty(
      '--brand-side-menu-background',
      this.resolveColorToken(this.ui.sideMenuColor) || 'var(--ion-color-dark)'
    );

    root.style.setProperty(
      '--brand-virtual-card-background',
      virtualCard.background || 'var(--ion-color-primary)'
    );
    root.style.setProperty(
      '--brand-virtual-card-background-image',
      virtualCard.backgroundImageFile
        ? `url("${this.getThemeAssetPath(virtualCard.backgroundImageFile)}")`
        : 'none'
    );
    root.style.setProperty(
      '--brand-virtual-card-text-color',
      virtualCard.textColor || 'var(--ion-color-primary-contrast)'
    );
    root.style.setProperty(
      '--brand-virtual-card-label-color',
      virtualCard.labelColor || 'var(--ion-color-primary-contrast)'
    );
    root.style.setProperty(
      '--brand-virtual-card-barcode-background',
      virtualCard.barcodeBackground || 'rgba(255, 255, 255, 0.9)'
    );
    root.style.setProperty(
      '--brand-virtual-card-border-color',
      virtualCard.borderColor || 'rgba(255, 255, 255, 0.24)'
    );
    root.style.setProperty(
      '--brand-virtual-card-logo-shadow',
      virtualCard.logoShadow || 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.35))'
    );
    root.style.setProperty('--brand-virtual-card-logo-top', virtualCard.logoTop || '12px');
    root.style.setProperty('--brand-virtual-card-logo-right', virtualCard.logoRight || '16px');
    root.style.setProperty('--brand-virtual-card-logo-width', virtualCard.logoWidth || '36%');
    root.style.setProperty('--brand-virtual-card-logo-height', virtualCard.logoHeight || '50px');
  }

  private resolveColorToken(colorToken: FlavorColorToken): string {
    switch (colorToken) {
      case 'primary':
        return 'var(--ion-color-primary)';
      case 'secondary':
        return 'var(--ion-color-secondary)';
      case 'tertiary':
        return 'var(--ion-color-tertiary)';
      case 'dark':
        return 'var(--ion-color-dark)';
      case 'medium':
        return 'var(--ion-color-medium)';
      case 'light':
        return 'var(--ion-color-light)';
      case 'white':
        return 'var(--ion-color-white)';
      case 'secondary-gradient':
        return 'var(--ion-color-secondary-gradient)';
      default:
        return '';
    }
  }
}
